/**
 * Channels management page
 *
 * Two per-channel switches live here and they are NOT the same thing
 * (decision D3/D3b, 2026-08-18):
 *  - "قناة المخطوطات" — `chats.is_manuscript_channel`, this app's visibility flag.
 *  - "المرآة" — whether the channel is mirrored to the backup archive. Mirror
 *    controls are admin-only (they drive aged Telegram accounts) and can only
 *    be switched on for a channel a poster account has already JOINED (D5).
 *
 * The rows come from /channel-annotations/chats-with-annotations (unfiltered),
 * not the bare chat list: it is the only list response that carries db_status,
 * notes and the cached file stats, which the filters below need.
 *
 * ~600 rows render unpaginated and unvirtualized. Filtering hides that; it does
 * not fix it.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateChat } from '@/hooks/useMessages';
import { useChatsWithAnnotations } from '@/hooks/useChannelAnnotations';
import { useChatLabel } from '@/hooks/useChatLabel';
import { useAuth } from '@/hooks/useAuth';
import {
  useMirrorChannels,
  useMirrorOnboarding,
  useToggleMirrorChannel,
} from '@/hooks/useMirror';
import { MirrorChannel, MirrorStatus } from '@/api/mirror';
import { getErrorMessage } from '@/api/client';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import MirrorCreateDialog from '@/components/mirror/MirrorCreateDialog';
import ChannelFilterBar from '@/components/channels/ChannelFilterBar';
import { ALL_CHANNEL_FILTERS, ChannelFilters, filterChannels } from '@/utils/channelFilters';
import { formatCount, formatFileSize } from '@/utils/formatters';
import DbStatusSelector from '@/pages/chatPreview/components/DbStatusSelector';
import ChannelNoteEditor from '@/pages/chatPreview/components/ChannelNoteEditor';

const MIRROR_STATUS_COLOR: Record<MirrorStatus, 'success' | 'default' | 'error'> = {
  ready: 'success',
  paused: 'default',
  broken: 'error',
};

const ChannelsPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const chatLabel = useChatLabel();
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const { data: chats = [], isLoading, isError, error } = useChatsWithAnnotations(false);
  const updateChatMutation = useUpdateChat();
  const [updatingChatId, setUpdatingChatId] = useState<number | null>(null);
  const [filters, setFilters] = useState<ChannelFilters>(ALL_CHANNEL_FILTERS);

  const visibleChats = useMemo(() => filterChannels(chats, filters), [chats, filters]);

  // Admin-only: every /mirror route is admin-gated, so a reviewer must not fire
  // a request that comes back 403.
  const { data: mirrors = [] } = useMirrorChannels(admin);
  const { data: onboarding } = useMirrorOnboarding(admin);
  const toggleMutation = useToggleMirrorChannel();

  const [createFor, setCreateFor] = useState<{ id: number; name: string | null } | null>(null);
  const [mirrorError, setMirrorError] = useState('');

  const mirrorBySource = useMemo(() => {
    const map = new Map<number, MirrorChannel>();
    for (const row of mirrors) {
      if (row.source_chats_id != null) map.set(row.source_chats_id, row);
    }
    return map;
  }, [mirrors]);

  /** D5: the enable control stays off until a poster account is a proven member. */
  const joinedChatIds = useMemo(
    () => new Set((onboarding?.memberships ?? []).map((m) => m.chats_id)),
    [onboarding]
  );

  const handleToggle = async (chatId: number, currentValue: boolean) => {
    setUpdatingChatId(chatId);
    try {
      await updateChatMutation.mutateAsync({
        id: chatId,
        is_manuscript_channel: !currentValue,
      });
    } finally {
      setUpdatingChatId(null);
    }
  };

  const handleMirrorToggle = async (row: MirrorChannel, next: boolean) => {
    setMirrorError('');
    try {
      await toggleMutation.mutateAsync({
        backupChannelId: row.backup_channel_id,
        enabled: next,
        accountId: row.poster_account_id ?? undefined,
      });
    } catch (e) {
      setMirrorError(getErrorMessage(e));
    }
  };

  if (isLoading) {
    return <LoadingSpinner message={t('channels.loading')} />;
  }

  if (isError) {
    return <ErrorAlert error={error} title={t('channels.errors.failedToLoad')} />;
  }

  const renderMirrorCell = (chatId: number, chatName: string | null) => {
    if (!admin) {
      return (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      );
    }

    const row = mirrorBySource.get(chatId);
    if (row) {
      const reasons = row.why_not.join(' · ');
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={reasons || t('mirror.allGatesGreen')}>
            <Chip
              size="small"
              color={MIRROR_STATUS_COLOR[row.status]}
              label={t(`mirror.status.${row.status}`)}
            />
          </Tooltip>
          <Switch
            size="small"
            checked={row.bc_status === 'ready'}
            onChange={(e) => handleMirrorToggle(row, e.target.checked)}
            disabled={toggleMutation.isPending}
            inputProps={{ 'aria-label': t('mirror.column') }}
          />
        </Box>
      );
    }

    const joined = joinedChatIds.has(chatId);
    return (
      <Tooltip title={joined ? t('mirror.enableHelp') : t('mirror.notJoined')}>
        {/* A disabled button swallows its own tooltip — the span keeps it. */}
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!joined}
            onClick={() => setCreateFor({ id: chatId, name: chatName })}
          >
            {t('mirror.enable')}
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('channels.title')}
      </Typography>

      {mirrorError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMirrorError('')}>
          {mirrorError}
        </Alert>
      )}

      {/* Same control as the chat-preview sidebar, media-category filters
          included — the predicate lives in utils/channelFilters.ts so both
          views agree. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <ChannelFilterBar filters={filters} onChange={setFilters} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {/* Isolated (FSI…PDI) like every other number in Arabic prose here. */}
          {t('channels.showing', {
            shown: `⁨${visibleChats.length}⁩`,
            total: `⁨${chats.length}⁩`,
          })}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('channels.columns.name')}</TableCell>
              <TableCell>{t('channels.columns.type')}</TableCell>
              <TableCell>{t('channels.columns.messages')}</TableCell>
              <TableCell>
                {/* The counts are a cache an admin refreshes by hand, and a file
                    with an unknown mime type is in none of the categories. */}
                <Tooltip title={t('channels.filesCacheNote')}>
                  <span>{t('channels.columns.files')}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{t('channels.columns.dbStatus')}</TableCell>
              <TableCell>{t('channels.columns.notes')}</TableCell>
              <TableCell>{t('channels.columns.manuscript')}</TableCell>
              <TableCell>{t('mirror.column')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleChats.map((chat) => {
              const isManuscript = Boolean(chat.is_manuscript_channel);
              const isUpdating = updateChatMutation.isPending && updatingChatId === chat.id;
              return (
                <TableRow key={chat.id} hover>
                  <TableCell>{chatLabel(chat)}</TableCell>
                  <TableCell>{chat.chat_type || '-'}</TableCell>
                  <TableCell>
                    <span dir="ltr">{chat.message_count ?? 0}</span>
                  </TableCell>
                  <TableCell>
                    {/* Counts and sizes stay LTR whatever the UI language. */}
                    <span dir="ltr">
                      {chat.file_count > 0
                        ? `${formatCount(chat.file_count)} · ${formatFileSize(chat.total_file_size)}`
                        : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DbStatusSelector
                      chatId={chat.id}
                      currentStatus={chat.annotation?.db_status || 'not_in_db'}
                      variant="plain"
                    />
                  </TableCell>
                  <TableCell>
                    <ChannelNoteEditor
                      chatId={chat.id}
                      currentNote={chat.annotation?.notes || null}
                      variant="plain"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={isManuscript}
                      onChange={() => handleToggle(chat.id, isManuscript)}
                      disabled={isUpdating}
                      inputProps={{
                        'aria-label': t('channels.columns.manuscript'),
                      }}
                    />
                  </TableCell>
                  <TableCell>{renderMirrorCell(chat.id, chat.chat_name)}</TableCell>
                </TableRow>
              );
            })}
            {visibleChats.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {chats.length === 0 ? t('channels.empty') : t('channels.noMatches')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {createFor && (
        <MirrorCreateDialog
          open
          chatId={createFor.id}
          chatName={createFor.name}
          onClose={() => setCreateFor(null)}
        />
      )}
    </Box>
  );
};

export default ChannelsPage;
